import { type ReactElement } from "react";
import { IntlProvider } from "react-intl";
import { Provider } from "react-redux";
import { BrowserRouter as Router } from "react-router-dom";
// import TestRenderer from 'react-test-renderer'; // does not work well since update (v16.4, npm/node upgrade, ...)
import TestRenderer from "react-test-renderer";
import { createStore } from "redux";

import { containsInLowerCase, getSimpleStringFromJsx } from "../../app-utils/string.js";
import { type DeletedGroup, type Group, type ProjectActivity, type User } from "../../graphql.js";
import messages from "../../messages/en-US.js";
import { ProjectActivityBody } from "./ProjectActivityBody.js";

/**
 * The search engine for project activities implemented as a service class.
 *   Use the static method matchFilter() to find matching activities.
 */

export class ProjectActivitySearchEngine {
  /*
  private static matchProject(project: IProject, term: string) {
    // has: creator name, date, prj name, prj description
    return !!project && (
      containsInLowerCase(project.name, term)
      || containsInLowerCase(project.description, term));
  }
  */

  private static matchView(view: any, term: string) {
    return !!view && (containsInLowerCase(view.description, term) || containsInLowerCase(view.owner, term));
  }

  private static matchGroup(group: Group, term: string) {
    return (
      !!group &&
      (containsInLowerCase(group.description, term) ||
        containsInLowerCase(group.owner, term) ||
        containsInLowerCase("" + group.itemCount, term))
    );
  }

  private static matchDeletedGroup(group: DeletedGroup, term: string) {
    return (
      !!group &&
      (containsInLowerCase(group.name, term) ||
        containsInLowerCase(group.typeDisplayName, term) ||
        containsInLowerCase("" + group.itemCount, term))
    );
  }

  private static matchUser(user: User, term: string): boolean {
    return !!user && containsInLowerCase(user.username, term);
  }

  private static matchUsersList(users: User[], term: string): boolean {
    return (
      !!users &&
      !!users.find((user) => {
        return ProjectActivitySearchEngine.matchUser(user, term);
      })
    );
  }

  private static matchTermAgainstJsxString(
    activity: ProjectActivity,
    term: string,
    isCurrentUserActivity: boolean,
    users: User[],
  ): boolean {
    /* create a first version of the activity content as a component so that we can filter by any text in it
     * <Router> is need because the <Link> component does require it
     * <IntlProvider> is needed because of text localisation
     *  <Provider store={store}> is needed to prevent the dispatch initialization to fail
     */
    const store = createStore(() => {});
    const activityComponent: ReactElement = TestRenderer.create(
      <Provider store={store}>
        <Router>
          <IntlProvider messages={messages} locale="en-US" defaultLocale="en-US">
            <ProjectActivityBody activity={activity} isCurrentUserActivity={isCurrentUserActivity} users={users} />
          </IntlProvider>
        </Router>
      </Provider>,
    ).toJSON();
    // create a simple string out of the JSX-formatted component in order to be able to filter all of the text
    const filterableText: string = getSimpleStringFromJsx(activityComponent);
    return containsInLowerCase(filterableText, term);
  }

  private static matchTermAgainstActivityProperties(activity: ProjectActivity, term: string): boolean {
    /*
     The search field considers all text that is visible for each entry in the panel:
     That should include all user observable data and also the attached meta data.
     */
    return (
      containsInLowerCase(activity.commentBody, term) || // search in comment text
      ProjectActivitySearchEngine.matchUser(activity.user, term) ||
      ProjectActivitySearchEngine.matchUsersList(activity.affectedUsers, term) ||
      activity.groups.map((group) => ProjectActivitySearchEngine.matchGroup(group, term)).find((b) => b) ||
      activity.deletedGroups
        .map((deleteGroup) => ProjectActivitySearchEngine.matchDeletedGroup(deleteGroup, term))
        .find((b) => b) ||
      activity.views.map((view) => ProjectActivitySearchEngine.matchView(view, term)).find((b) => b)
      // || ProjectActivitySearchEngine.matchProject(activity.project, term)
    );
  }

  /**
   * Search for all terms of the termList in the current activity.
   *   It returns true if all terms are present (AND logic).
   *   The search field considers all text that is visible for each entry in the panel.
   *   That includes all user observable data and also the attached meta data.
   *   The current user is translated to the string 'you' in the JSX text representation.
   *
   * @param activity                the project activity object
   * @param termList                a list of strings with the terms
   * @param users
   * @param isCurrentUserActivity   is true if this activity is for the current user (for searching 'you')
   * @returns {boolean}             true if all terms are present in the activity.
   */
  static matchFilter(
    activity: ProjectActivity,
    termList: string[],
    users: User[],
    isCurrentUserActivity = false,
  ): boolean {
    // An activity must match all filters to pass.
    for (const term of termList) {
      if (
        !ProjectActivitySearchEngine.matchTermAgainstActivityProperties(activity, term) &&
        // search in component text including labels and JSX as last resort
        !ProjectActivitySearchEngine.matchTermAgainstJsxString(activity, term, isCurrentUserActivity, users)
      ) {
        return false;
      }
    }
    return true;
  }
}
